import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken } from "../lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [guest, setGuestState] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      const g = localStorage.getItem("guest_mode");
      setGuestState(g === "1");
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api("/auth/me");
      if (!me.public_id) {
        try {
          const r = await api("/me/public-id");
          if (r?.public_id) me.public_id = r.public_id;
        } catch {}
      }
      setUser(me);
      setGuestState(false);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const res = await api("/auth/login-universal", { method: "POST", body: JSON.stringify({ identifier, password }) });
    setToken(res.token);
    localStorage.removeItem("guest_mode");
    setGuestState(false);
    setUser(res.user);
  };

  const register = async (name, email, password) => {
    const res = await api("/auth/register", { method: "POST", body: JSON.stringify({ name, email, password }) });
    setToken(res.token);
    localStorage.removeItem("guest_mode");
    setGuestState(false);
    setUser(res.user);
  };

  const registerPhone = async (name, phone, password, email) => {
    const body = { name, phone, password };
    if (email && email.trim()) body.email = email.trim();
    const res = await api("/auth/register-phone", { method: "POST", body: JSON.stringify(body) });
    setToken(res.token);
    localStorage.removeItem("guest_mode");
    setGuestState(false);
    setUser(res.user);
  };

  const loginWithGoogle = async (credential) => {
    const res = await api("/auth/google", { method: "POST", body: JSON.stringify({ credential }) });
    setToken(res.token);
    localStorage.removeItem("guest_mode");
    setGuestState(false);
    setUser(res.user);
  };

  const setRole = async (role, extra) => {
    const res = await api("/auth/role", { method: "POST", body: JSON.stringify({ role, ...(extra || {}) }) });
    setUser(res);
  };

  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch {}
    clearToken();
    localStorage.removeItem("guest_mode");
    setGuestState(false);
    setUser(null);
  };

  const setGuest = async (val) => {
    if (val) {
      localStorage.setItem("guest_mode", "1");
      setGuestState(true);
    } else {
      localStorage.removeItem("guest_mode");
      setGuestState(false);
    }
  };

  return (
    <Ctx.Provider value={{ user, guest, loading, login, register, registerPhone, loginWithGoogle, setRole, setGuest, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
